<template>
  <v-container fill-height justify-content-start class="app-section-width pa-0">
    <v-layout column>
      <v-layout flat class="activity-header error">
        <v-layout align-center>
          <v-icon color="error-text" class="ms-3 me-3 display-2">
            mdi-alert-circle-outline
          </v-icon>

          <v-toolbar-title class="error-text--text headline">
            {{ isFailedPayment ? $t('error.title.failed_payment') : $t('activity_reservation.error.error') }}
          </v-toolbar-title>
        </v-layout>
      </v-layout>

      <v-layout column fill-height justify-center class="pa-5 text-center">
        <span v-if="isFailedPayment" class="subtitle-1">
          {{ $t('error.text.failed_payment') }}
        </span>
        <span v-else-if="getCapacityStatus({ over: true })" class="subtitle-1">
          {{ $t("activity_reservation.over_capacity") }}
        </span>
        <span v-else-if="errorString">
          <span class="subtitle-1">
            {{ errorString }}
          </span>
        </span>
        <v-layout v-else column>
          <span class="subtitle-1">
            {{ $t("activity_reservation.error.generic_error_1") }}
          </span>
          <span class="subtitle-2 mt-3">
            {{ $t("activity_reservation.error.generic_error_2") }}
          </span>
        </v-layout>
      </v-layout>

      <v-layout class="all-gap-10 pa-5" wrap>
        <v-flex>
          <v-btn
            @click="
              () => {
                reservationMethodType === 'IN_VENUE'
                  ? $router.go(-2)
                  : $router.go(smAndDown ? -3 : -2);
              }
            "
            outlined
            block
            color="primary"
            class="primary-gradient"
          >
            {{ $t("activity_reservation.error.back") }}
          </v-btn>
        </v-flex>

        <v-flex v-if="canRetry || isFailedPayment">
          <v-btn
            @click="retry()"
            outlined
            :loading="pendingConfirmation"
            block
            color="primary"
            class="primary-gradient"
          >
            {{ $t("activity_reservation.error.retry") }}
        </v-btn>
        </v-flex>
      </v-layout>
    </v-layout>
  </v-container>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import routeNames from '@/router/routes'
import reservationMixin from '@/mixins/Reservation'
import activityMixins from '@/mixins/Activity'
import vuetifyMixins from '@/mixins/Vuetify'
import { ActivityReservationsRequests } from '@/services/activityReservations.requests'

const activityReservationsService = new ActivityReservationsRequests()

export default {
  beforeMount () {
    if (!this.state.error) {
      this.$router.go(-1)
    }
  },
  data () {
    return {
      routeNames,
      pendingConfirmation: false,
      canRetry: true,
    }
  },
  computed: {
    ...mapGetters({
      getDashboardName: 'service/getDashboardName',
      getOrder: 'cart/getOrder',
    }),
    ...mapState(['activityReservations', 'state', 'pages']),
    isFailedPayment () {
      if (this.state.error.body.errors && this.state.error.body.errors.length) {
        const { errors = [] } = this.state.error.body
        const error = errors[0]

        return error.name === 'failed_payment'
      }

      return false
    },
    errorString () {
      if (!this.$route.params.reservationMethod) {
        return this.$t('error.payment_error')
      }

      let errorString

      const reservationMethod = this.$route.params.reservationMethod

      if (this.state.error.body.errors && this.state.error.body.errors.length) {
        const { errors = [] } = this.state.error.body
        const error = errors[0]
        if ( this.$te(`error.${ error.name }.${ reservationMethod.type }`) ) {
          errorString = this.$t(`error.${ error.name }.${ reservationMethod.type }`)
        } else if (this.$te(`error.${ error.name }.${ error.value }.${ reservationMethod.type }`)) {
          errorString = this.$t(`error.${ error.name }.${ error.value }.${ reservationMethod.type }`)
        } else if (this.$te(`error.${ error.name }.${ error.value }`)) {
          errorString = this.$t(`error.${ error.name }.${ error.value }`)
        } else if (this.$te(`error.${ error.name }`) && !error.value) {
          errorString = this.$t(`error.${ error.name }`)
        } else if (this.$te(`error.${ error.name }.generic`)) {
          errorString = this.$t(`error.${ error.name }.generic`)
        } else {
          errorString = this.$t('error.payment_error')
        }
      } else {
        const errors = this.state.error.body
        if (Array.isArray(errors)) {
          if (errors.some(e => e.error_code === 'NOT_ENOUGH_BALANCE')) {
            errorString = this.$t('error.not_enough_balance')
          } else if (errors.some(e => e.error_code === 'DISABLED_ACCOUNT')) {
            errorString = this.$t('error.disabled_account')
          } else if (errors.some(e => e.error_code === 'DISABLED_GUEST')) {
            errorString = this.$t('error.disabled_guest')
          } else if (errors.some(e => e.error_code === 'LIMIT_EXCEEDED')) {
            errorString = this.$t('error.limit_exceded')
          } else if (
            errors.some(e =>
              [
                'ILLEGAL_RELATED_TRANSACTIONS',
                'MIXED_TRANSACTION_TYPES',
                'MULTIPLE_ACCOUNTS_WITH_PAYMENT_GATEWAYS',
                'MULTIPLE_PAYMENT_GATEWAYS_FOR_SAME_ACCOUNT',
                'MULTIPLE_PAYMENT_GATEWAYS_ON_RELATED_TRANSACTIONS',
                'PAYMENT_GATEWAY_COMMUNICATION_ERROR',
                'TRANSACTION_ALREADY_EXISTS',
              ].includes(e.error_code)
            )
          ) {
            errorString = this.$t('error.payment_error')
          }
        }
      }
      return errorString
    },
  },
  methods: {
    retry () {
      if (this.isFailedPayment) {
        this.retryPayment()
      } else {
        this.retryReservation()
      }
    },

    retryReservation () {
      this.getSlotCapacity({ 
        activityId: this.pages.page.pageSpecific.activityId,
      }).then(() => {
        if (this.getCapacityStatus({ over: true })) return
        this.pendingConfirmation = true
        this.$store
          .dispatch('activityReservations/retryReservation')
          .then((data) => {
            if (data.redirectUrl) {
              this.$router.push({ name: routeNames.reservationPayment.name, params: { redirectUrl: data.redirectUrl } })
            } else {
              this.$router.push({ name: routeNames.reservationComplete.name })
            }
          })
          .catch(error => {
            this.$store.commit('state/setError', error)
          })
          .finally(() => {
            this.pendingConfirmation = false
          })
      })
    },

    retryPayment () {
      const paymentMethodsEnabled = this.allPaymentMethods.filter(({ enabled }) => enabled)

      if (paymentMethodsEnabled.length === 1) {
        const params = {
          activityReservationId: this.$route.params.activityReservationId,
          paymentServiceId: this.$route.params.paymentServiceId,
        }

        activityReservationsService.retryPayment(params.activityReservationId, params.paymentServiceId)
          .then((data) => {
            this.$router.replace({ name: routeNames.reservationPayment.name, params: { redirectUrl: data.redirectUrl, ...params } })
          })
          .catch((error) => {
            this.$store.commit('state/setError', error)
            this.$router.replace({ name: routeNames.reservationError.name, params: { reservationMethod: this.reservationMethod } })
          })
      } else {
        const activityReservationId = this.$route.params.activityReservationId
        this.$router.replace({ name: routeNames.paymentMethods.name, params: { activityReservationId } })
      }
    },
  },
  async created () {
    // Refresh account data
    await this.$store.dispatch('me/getBookings')
    this.$store.dispatch('accounts/listMyAccounts').then(accounts => {
      if (this.activityReservations.edited && this.activityReservations.edited.account && this.activityReservations.edited.account.id) {
        const accountId = this.activityReservations.edited.account.id
        this.$store.commit('activityReservations/setField', {
          field: 'account',
          value: accounts.find(account => account.id === accountId),
        })
      }
    })
  },
  mixins: [reservationMixin, activityMixins, vuetifyMixins],
}
</script>

<style scoped lang="scss">
.notranslate {
  transform: none !important;
}
.activity-header {
  max-height: 90px;
}
</style>
